import {
    Button,
    ColumnLayout,
    Grid,
    Header,
    MultiselectProps,
    SpaceBetween
} from "@amzn/awsui-components-react";
import * as React from "react";
import { UIField } from "../../common/UIField";
import { StaticListSelect } from "../../common/StaticListSelect";
import { LegalContactTable } from "../LegalContactTable";
import { LegalContact, WhosMyLawyerContent } from "@amzn/ask-legal-domain";
import { WhosMyLawyerFactory } from "../../../factory/whos-my-lawyer-factory";
import { LegalContactsPolarisFactory } from "../../../factory/polaris/legal-contacts-polaris-factory";
import { AppContext } from "../../../setup/context";
import { Preference } from "../../../setup/preference";

export const ViewByLegalTeams = (props: {
    items: LegalContact.Record[];
    content: WhosMyLawyerContent;
    loading?: boolean;
}) => {
    const context = React.useContext(AppContext);
    const [selectedTeams, setSelectedTeams] = React.useState<string[]>([]);
    const [selectedSubTeams, setSelectedSubTeams] = React.useState<string[]>([]);

    const resolveTeamOptions = () => {
        return [...new Set(props.items.map(x => x.legalTeamOptions?.[0]?.displayValue))]
            .filter(t => t?.trim().length);
    };
    const resolveSubTeamOptions = () => {
        return [...new Set(props.items.map(x => x.legalSubTeamOptions).flat().map(t => t?.displayValue))]
            .filter(t => t?.trim().length);
    };

    const filterRecords = (records: LegalContact.Record[]): LegalContact.Record[] => {
        if (selectedTeams.length === 0 && selectedSubTeams.length === 0) return records;
        return records.filter(x => {
            const expandedRow = x as LegalContactsPolarisFactory.Table.ExpandedLegalContactRow;

            // Determine matches
            let teamMatch = selectedTeams.length === 0;
            let subTeamMatch = selectedSubTeams.length === 0;

            // Check team match
            if (selectedTeams.length > 0) {
                if (expandedRow._primaryFieldKey === "legalTeams") {
                    const value = expandedRow._expandedPrimaryValue;
                    teamMatch = value && typeof value === "object" && "displayValue" in value
                        && selectedTeams.includes(value.displayValue);
                } else {
                    const team = x.legalTeamOptions?.[0];
                    teamMatch = team?.displayValue && selectedTeams.includes(team.displayValue);
                }
            }

            // Check subteam match
            if (selectedSubTeams.length > 0) {
                if (expandedRow._primaryFieldKey === "legalSubTeams") {
                    const value = expandedRow._expandedPrimaryValue;
                    subTeamMatch = value && typeof value === "object" && "displayValue" in value
                        && selectedSubTeams.includes(value.displayValue);
                } else {
                    const subTeams = x.legalSubTeamOptions ?? [];
                    subTeamMatch = subTeams.some(y => selectedSubTeams.includes(y.displayValue));
                }
            }

            return teamMatch && subTeamMatch;
        });
    };

    return <SpaceBetween size="xxl">
        <Grid gridDefinition={[
            { colspan: 3 },
            { colspan: 9 }
        ]}>
            <ColumnLayout columns={1}>
                <Header>Filter By Legal Team</Header>
                <UIField.CustomField
                    name="Legal Team"
                    child={
                        <StaticListSelect.Multi<string>
                            options={resolveTeamOptions()}
                            fromOption={(option: MultiselectProps.Option) => option.value}
                            toOption={(team: string) => ({ label: team, value: team })}
                            onSelectChange={(options: string[]) => setSelectedTeams(options)}
                            selected={selectedTeams}
                            disabled={props.loading}
                            sortingFunction={(o) => o.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))}
                        />
                    }
                />
                <UIField.CustomField
                    name="Legal Sub-Team"
                    child={
                        <StaticListSelect.Multi<string>
                            options={resolveSubTeamOptions()}
                            fromOption={(option: MultiselectProps.Option) => option.value}
                            toOption={(team: string) => ({ label: team, value: team })}
                            onSelectChange={(options: string[]) => setSelectedSubTeams(options)}
                            selected={selectedSubTeams}
                            disabled={props.loading}
                            sortingFunction={(o) => o.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))}
                        />
                    }
                />
                {(selectedTeams.length > 0 || selectedSubTeams.length > 0) && <Button variant="normal" onClick={() => {
                    setSelectedTeams([]);
                    setSelectedSubTeams([]);
                }}>Reset Filters</Button>}
            </ColumnLayout>
            <LegalContactTable
                items={filterRecords(props.items)}
                loading={props.loading}
                defaultSortState={WhosMyLawyerFactory.getSortingStateFromContent(props.content)}
                defaultPreference={WhosMyLawyerFactory.toPreferencesFromDisplayConfiguration(
                    props.content.defaultConfiguration,
                    props.content
                )}
                content={props.content}
            />
        </Grid>
    </SpaceBetween>;
};